import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import startCase from 'lodash/startCase';
import styled from 'styled-components';
import { AlertContent, Avatar, Button, InputCharCounter, Label, List, ListItem, ModalContent, ModalFooter, ModalHeader, ModalLayout, ModalTitle, StatusPayment, Text, Tooltip } from 'components';
import { Size } from 'design/types';
import { routeForUserId } from 'routes';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { cancelPayoutRequest, Payouts, retryPayoutRequest } from 'store/Collections/Payouts';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IBaseSite, ICancelPayoutOptions, IPayout, PayoutStatus } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay, formatJobTitle } from 'utils';
import { HeaderContent, PayoutCard, PayoutCardRow, PayoutTimestamp, StyledFormError, Subtitle } from './styles';

interface IPayoutDetailsProps {
  payout: IPayout;
  payoutSite?: IBaseSite;
  onClose: () => void;
  onUpdate?: () => void;
}

const AlignRight = styled.span<{ capitalize?: boolean }>`
  max-width: 90%;
  word-break: break-word;
  text-align: right;
  text-transform: ${({ capitalize }) => (capitalize ? 'capitalize' : 'inherit')};
`;

const TextWrap = styled.div`
  word-break: break-word;
`;

const PayoutDetails = ({ payout, payoutSite, onClose, onUpdate }: IPayoutDetailsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [confirmCancel, setConfirmCancel] = useState<boolean>(false); // State for UI for confirming cancel
  const [hasCancelError, setHasCancelError] = useState<boolean>(false); // Confirm cancel error state
  const [cancelReason, setCancelReason] = useState<string | undefined>(undefined);
  const retryErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payouts.RETRY_PAYOUT_REQUEST)) as IApiErrorPayloadWithCode;
  const cancelErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payouts.CANCEL_PAYOUT_REQUEST)) as IApiErrorPayloadWithCode;
  const retryIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.RETRY_PAYOUT_REQUEST));
  const cancelIsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.CANCEL_PAYOUT_REQUEST));

  useEffect(
    () => () => {
      dispatch(clearRequest(Payouts.RETRY_PAYOUT_REQUEST));
      dispatch(clearRequest(Payouts.CANCEL_PAYOUT_REQUEST));
    },
    [dispatch]
  );

  const lastUpdatedDate = payout?.updatedAt ? dayjs(payout.updatedAt).local().format('MM-DD-YYYY') : '--';
  const lastUpdatedTime = payout?.updatedAt ? dayjs(payout.updatedAt).local().format('HH:mm A z') : '--';
  const submittedDate = payout?.createdAt ? dayjs(payout.createdAt).local().format('MM-DD-YYYY') : '--';
  const submittedTime = payout?.createdAt ? dayjs(payout.createdAt).local().format('HH:mm A z') : '--';
  const sentBy = (payout?.statusHistory || []).find((status) => status.action === 'create')?.changedBy?.name || '';

  const retryPayment = () => {
    dispatch(retryPayoutRequest(payout.id, handleOnUpdate));
  };

  const cancelPayment = () => {
    setConfirmCancel(true);
  };

  const confirmCancelPayment = (options: ICancelPayoutOptions) => {
    dispatch(cancelPayoutRequest(payout.id, options, handleOnUpdate));
  };

  const gotoEmployeeDetails = () => {
    history.push(routeForUserId(payout.employee.id));
  };

  const handleOnUpdate = () => {
    onClose();
    onUpdate?.();
  };

  const onInputChange = (inputValues: { value: string; error: boolean }) => {
    const { value, error } = inputValues;
    if (hasCancelError !== error) setHasCancelError(error);
    setCancelReason(value);
  };

  // Footer
  const getFooter = (() => {
    if ((payout && payout.status === PayoutStatus.EXPIRED) || (payout && payout.status === PayoutStatus.PENDING)) {
      return (
        <ModalFooter columns={2}>
          <Button data-cy="cancel-payment-btn" appearance="white" label="Cancel Payment" isLoading={retryIsLoading || cancelIsLoading} onClick={cancelPayment} />
          <Button data-cy="retry-payment-btn" label="Retry Payment" disabled={cancelIsLoading} isProcessing={retryIsLoading} onClick={() => retryPayment()} />
        </ModalFooter>
      );
    }
    if (((payout && payout.status === PayoutStatus.CANCELED) || (payout && payout.status === PayoutStatus.PAID)) && payout && payout.employee.deletedAt === undefined) {
      return (
        <ModalFooter columns={1}>
          <Button
            appearance="primary"
            size="large"
            data-cy="view-all-transactions-btn"
            label="View All Transactions"
            onClick={() => {
              onClose();
              gotoEmployeeDetails();
            }}
          />
        </ModalFooter>
      );
    }
    return null;
  })();

  // Date Last X
  const DateLastActivity = (() => {
    if (payout && payout.status === PayoutStatus.CANCELED) {
      return <ListItem label="Date Canceled" data-cy="date-canceled" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
    }
    if (payout && payout.status === PayoutStatus.PAID) {
      return <ListItem label="Date Paid" data-cy="date-paid" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
    }
    return <ListItem label="Date Last Attempt" data-cy="date-last-attempt" value={<PayoutTimestamp date={lastUpdatedDate || '--'} time={lastUpdatedTime || '--'} />} />;
  })();

  const cancelReasonInput = (() => <InputCharCounter countMax={50} maxLength={50} onInputChange={onInputChange} />)();

  return confirmCancel ? (
    <AlertContent
      data-cy="cancel-payment-modal"
      title="Cancel payment?"
      message="Are you sure you want to cancel this payment? This action cannot be undone."
      leftActionLabel="Back"
      onLeftAction={() => setConfirmCancel(false)}
      rightActionLabel="Cancel Payment"
      isLoading={cancelIsLoading}
      onRightAction={() => confirmCancelPayment({ cancelReason })}
    >
      {cancelReasonInput}
      {cancelErrors?.errorMessages?.map((error, index) => (
        <StyledFormError key={`error-message-${index}`} data-cy="form-error">
          {error}
        </StyledFormError>
      ))}
    </AlertContent>
  ) : (
    <ModalLayout maxWidth={540} isActive onClose={onClose} data-cy="transaction-details-modal">
      <ModalHeader>
        <HeaderContent gridGap={22}>
          <Avatar
            size="modal"
            data-cy="avatar"
            hasLink
            id={(!payout?.employee.deletedAt && payout?.employee.id) || ''}
            userId={payout.userId}
            username={payout?.employee.name || ''}
            isLoading={!payout}
            isDismissed={!!payout?.employee.deletedAt}
          />

          <HeaderContent>
            <ModalTitle data-cy="modal-title">{payout?.employee.name || ''}</ModalTitle>
            <Subtitle data-cy="modal-subtitle">{payout?.id || ''}</Subtitle>
          </HeaderContent>
        </HeaderContent>

        <PayoutCardRow>
          <PayoutCard>
            <Label>Net Total</Label>
            <Text data-cy="net-total">{!isNull(payout?.net) ? centsToDollarsDisplay((payout as IPayout).net) : '--'}</Text>
          </PayoutCard>

          <PayoutCard data-cy="transaction-card" data-tooltip-id={`${payout?.id}-status-tooltip`}>
            <Label>Status</Label>

            {payout && (
              <>
                <StatusPayment size={Size.LARGE} status={payout.status} />
                {payout.alert && (
                  <Tooltip id={`${payout.id}-status-tooltip`} place="bottom" wrapper="p">
                    <strong>{startCase(payout.alert?.label)}</strong>
                    <br />
                    {payout.alert?.message}
                  </Tooltip>
                )}
              </>
            )}
          </PayoutCard>
        </PayoutCardRow>
      </ModalHeader>

      <ModalContent>
        <List>
          <ListItem label="Date Submitted" data-cy="date-submitted" value={<PayoutTimestamp date={submittedDate || '--'} time={submittedTime || '--'} />} />
          {DateLastActivity}
          <ListItem label="Tips" data-cy="tips" value={!isNil(payout?.gross) && !isNil(payout?.autoGratuity) ? centsToDollarsDisplay(payout.gross - payout.autoGratuity) : '--'} />
          <ListItem label="Autograts" data-cy="autogratuity" value={!isNil(payout?.autoGratuity) ? centsToDollarsDisplay(payout.autoGratuity) : '--'} />

          {payout && payout.status === PayoutStatus.CANCELED && (
            <ListItem label="Reason for Cancellation" data-cy="cancel-reason" accordion>
              {payout.cancelReason || 'No reason provided.'}
            </ListItem>
          )}

          <ListItem label="Gross" data-cy="gross" value={!isNil(payout?.gross) ? centsToDollarsDisplay((payout as IPayout).gross) : '--'} />
          <ListItem label="Holdback" data-cy="holdbacks" value={!isNil(payout?.holdback) ? centsToNegativeDollarsDisplay((payout as IPayout).holdback) : '--'} />
          <ListItem label="Net" data-cy="net" value={!isNil(payout?.net) ? centsToDollarsDisplay((payout as IPayout).net) : '--'} />
          <ListItem label="Site" data-cy="site" value={payout.site?.name || payoutSite.name ? <AlignRight>{payout.site?.name || payoutSite.name}</AlignRight> : '--'} />
          <ListItem label="Disbursement Method" data-cy="disbursement-method" value={payout?.disbursementMethod ? <AlignRight capitalize>{payout.disbursementMethod}</AlignRight> : '--'} />
          <ListItem label="Job Title" data-cy="job-title" value={payout && formatJobTitle(payout.job.title, payout.job.classification)} />
          <ListItem data-cy="sent-by" label="Sent By" value={sentBy ? <AlignRight>{sentBy}</AlignRight> : '--'} />
          <ListItem data-cy="notes" label="Notes" accordion>
            <TextWrap>{payout?.notes || 'No notes provided.'}</TextWrap>
          </ListItem>
          {payout?.approvalCheck.statusDesc && (
            <ListItem data-cy="review-notes" label="Reviewer's Note" accordion>
              <TextWrap>{payout?.approvalCheck.statusDesc}</TextWrap>
            </ListItem>
          )}
        </List>
      </ModalContent>

      {retryErrors?.errorMessages?.map((error, index) => (
        <StyledFormError key={`error-message-${index}`} data-cy="form-error">
          {error}
        </StyledFormError>
      ))}

      {getFooter}
    </ModalLayout>
  );
};

export default PayoutDetails;
